/*
 * Copyright (c) 2026 CalAmp Corp. All Rights Reserved
 */
import {
  useCallback,
  useState
} from 'react';
import isNil from 'lodash.isnil';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';

const SENTINEL_BADGE_DISMISSED_PREFERENCE = 'admin-devices.sentinelAssociation.newBadgeDismissed';

let cachedDismissed: boolean | undefined;

// test helper to reset module-level cache
export const resetSentinelAssociationNewBadgeCache = () => {
  cachedDismissed = undefined;
};

const isSentinelBadgeDismissed = (): boolean => {
  if (!isNil(cachedDismissed)) {
    return cachedDismissed;
  }
  cachedDismissed = UserPreferences.isInitialized()
    ? !!UserPreferences.get(SENTINEL_BADGE_DISMISSED_PREFERENCE)
    : false;
  return cachedDismissed;
};

const dismissSentinelBadge = () => {
  if (isSentinelBadgeDismissed()) {
    return;
  }
  cachedDismissed = true;
  UserPreferences.set(SENTINEL_BADGE_DISMISSED_PREFERENCE, true, true);
};

/**
 * Manages one-time NEW badge behavior for the Sentinel Association tab.
 * The badge is shown until a user visits the tab and closes the dialog once,
 * after which it remains dismissed indefinitely.
 */
export const useSentinelAssociationNewBadge = (hasSentinelAssociation: boolean) => {
  const [isDismissed, setIsDismissed]           = useState(() => isSentinelBadgeDismissed());
  const [visitedInSession, setVisitedInSession] = useState(false);

  const onSentinelTabViewed = useCallback(() => {
    if (hasSentinelAssociation && !isDismissed) {
      setVisitedInSession(true);
    }
  }, [hasSentinelAssociation, isDismissed]);

  const onDialogClose = useCallback(() => {
    if (visitedInSession && !isDismissed) {
      dismissSentinelBadge();
      setIsDismissed(true);
    }
  }, [visitedInSession, isDismissed]);

  return {
    showNewBadge : hasSentinelAssociation && !isDismissed,
    onSentinelTabViewed,
    onDialogClose
  };
};

export default useSentinelAssociationNewBadge;
